import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { TabPanel } from '@mui/lab';
import { Grid, Card, CardHeader, CardContent, Box, Typography, Button, Pagination, CircularProgress, IconButton } from '@mui/material';
import { Description, AttachMoney, People, CalendarToday, HourglassEmpty, Warning } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getInvoices, getInvoiceSummary } from '../redux/ducks/invoices';
import { getCharges } from '../redux/ducks/charges';
import StatCard from './StatCard';
import { formatDate, formatDayShort, toLocalDay } from '../utils/dateUtils';

const ErrorFallback = styled.span`
    color: rgba(0, 0, 0, 0.38);
    font-weight: 400;
`;

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(3),
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const InvoiceList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const InvoiceListRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const InvoiceListRowMeta = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

const RightAligned = styled(Box)({
    textAlign: 'right',
});

const PaginationRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
}));

const currency = (value) => (value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const invoiceCount = (n) => `${n || 0} ${(n || 0) === 1 ? "invoice" : "invoices"}`;

const BillingOverview = ({ tabValue }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [upcomingCharges, setUpcomingCharges] = useState([]);
    const [invoicePage, setInvoicePage] = useState(1);
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [isLoadingCharges, setIsLoadingCharges] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [summary, setSummary] = useState({
        lastMonthRevenue: 0,
        lastMonthPaidInvoicesCount: 0,
        pendingInvoices: 0,
        pendingAmount: 0,
        activeClients: 0,
        recurringRevenue: 0,
        lastMonthPendingRevenue: 0,
        lastMonthPendingInvoicesCount: 0,
        overdueRevenue: 0,
        overdueInvoicesCount: 0,
        lastMonthActiveClients: 0,
        avgDaysToPayment: 0,
        lastMonthPartialRevenue: 0,
        lastMonthPartiallyPaidCount: 0,
        partialRevenue: 0,
        partiallyPaidCount: 0,
        outstandingAmount: 0
    });
    const loadStatusRef = useRef({ invoices: 'pending', charges: 'pending' });
    const [loadTick, setLoadTick] = useState(0);
    const [invoicesError, setInvoicesError] = useState(false);
    const [chargesError, setChargesError] = useState(false);
    const recurringRevenueRef = useRef(0);
    const pageSize = 20;

    useEffect(() => {
        if (tabValue !== 'overview') return;
        loadStatusRef.current = { invoices: 'pending', charges: 'pending' };
        setInvoicesError(false);
        setChargesError(false);
        setInitialLoadComplete(false);
        setInvoicePage(1);
        loadInvoices(1);
        loadCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue]);

    useEffect(() => {
        if (tabValue !== 'overview') return;
        if (!initialLoadComplete) return;
        loadInvoices(invoicePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoicePage]);

    useEffect(() => {
        if (loadTick === 0) return;
        setSummary((prev) => ({ ...prev, recurringRevenue: recurringRevenueRef.current }));
        setInitialLoadComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadTick]);

    const markLoaded = (key, status) => {
        loadStatusRef.current[key] = status;
        if (loadStatusRef.current.invoices !== 'pending' && loadStatusRef.current.charges !== 'pending') {
            setLoadTick((t) => t + 1);
        }
    };

    const loadInvoices = (page = 1) => {
        setIsLoadingInvoices(true);
        const start = (page - 1) * pageSize;
        dispatch(getInvoices(start, pageSize, null, (response) => {
            if (response && response.invoices) {
                setRecentInvoices(response.invoices);
                setInvoiceTotal(response.total);
            } else {
                setRecentInvoices([]);
                setInvoiceTotal(0);
            }
        }));
        // Summary metrics (server-side aggregation)
        dispatch(getInvoiceSummary((data) => {
            setIsLoadingInvoices(false);
            if (data) {
                setSummary((prev) => ({
                    ...prev,
                    lastMonthRevenue: data.lastMonthRevenue || 0,
                    lastMonthPaidInvoicesCount: data.lastMonthPaidInvoicesCount || 0,
                    pendingInvoices: data.pendingInvoices || 0,
                    pendingAmount: data.pendingAmount || 0,
                    activeClients: data.activeClients || 0,
                    lastMonthPendingRevenue: data.lastMonthPendingRevenue || 0,
                    lastMonthPendingInvoicesCount: data.lastMonthPendingInvoicesCount || 0,
                    overdueRevenue: data.overdueRevenue || 0,
                    overdueInvoicesCount: data.overdueInvoicesCount || 0,
                    lastMonthActiveClients: data.lastMonthActiveClients || 0,
                    avgDaysToPayment: data.avgDaysToPayment || 0,
                    lastMonthPartialRevenue: data.lastMonthPartialRevenue || 0,
                    lastMonthPartiallyPaidCount: data.lastMonthPartiallyPaidCount || 0,
                    partialRevenue: data.partialRevenue || 0,
                    partiallyPaidCount: data.partiallyPaidCount || 0,
                    outstandingAmount: data.outstandingAmount || 0
                }));
                setInvoicesError(false);
                markLoaded('invoices', 'loaded');
            } else {
                setInvoicesError(true);
                markLoaded('invoices', 'error');
            }
        }));
    };

    const loadCharges = () => {
        setIsLoadingCharges(true);
        dispatch(getCharges((response) => {
            setIsLoadingCharges(false);
            const list = response && Array.isArray(response.charges) ? response.charges : null;
            if (list) {
                const recurring = list.filter((charge) =>
                    charge && charge.recurring === true && charge.active === true && charge.date
                );
                const now = new Date();
                const upcoming = recurring.map((charge) => {
                    try {
                        const chargeDay = formatDate(charge.date);
                        if (!chargeDay) return null;
                        const chargeDate = new Date(`${chargeDay}T00:00:00`);
                        if (isNaN(chargeDate.getTime())) return null;
                        const anchorDay = chargeDate.getDate();
                        let nextDate = new Date(chargeDate);
                        while (nextDate <= now) {
                            if (charge.interval === 'Month') {
                                const target = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 1);
                                const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
                                target.setDate(Math.min(anchorDay, lastDay));
                                nextDate = target;
                            } else if (charge.interval === 'Week') {
                                nextDate.setDate(nextDate.getDate() + 7);
                            } else if (charge.interval === 'Year') {
                                const target = new Date(nextDate.getFullYear() + 1, nextDate.getMonth(), 1);
                                const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
                                target.setDate(Math.min(anchorDay, lastDay));
                                nextDate = target;
                            } else {
                                break;
                            }
                        }
                        return { ...charge, nextDate: toLocalDay(nextDate) };
                    } catch {
                        return null;
                    }
                }).filter((charge) => {
                    if (!charge || !charge.nextDate) return false;
                    try {
                        const nextDate = new Date(charge.nextDate);
                        const thirtyDaysFromNow = new Date(now);
                        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
                        return nextDate <= thirtyDaysFromNow;
                    } catch {
                        return false;
                    }
                }).sort((a, b) => {
                    try { return new Date(a.nextDate) - new Date(b.nextDate); } catch { return 0; }
                });
                setUpcomingCharges(upcoming);

                recurringRevenueRef.current = recurring.reduce((sum, charge) => sum + (charge.amount || 0), 0);
                setChargesError(false);
                markLoaded('charges', 'loaded');
            } else {
                setUpcomingCharges([]);
                recurringRevenueRef.current = 0;
                setChargesError(true);
                markLoaded('charges', 'error');
            }
        }));
    };

    const handleInvoicePageChange = (event, value) => {
        setInvoicePage(value);
    };

    return (
      <StyledTabPanel value="overview">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              icon={<AttachMoney fontSize="small" />}
              label="Last Month Revenue"
              value={invoicesError ? <ErrorFallback>—</ErrorFallback> : currency(summary.lastMonthRevenue)}
              sub={
                  summary.lastMonthPartialRevenue > 0
                      ? `${invoiceCount(summary.lastMonthPaidInvoicesCount)} paid · plus ${currency(summary.lastMonthPartialRevenue)} part paid on ${invoiceCount(summary.lastMonthPartiallyPaidCount)}`
                      : `${invoiceCount(summary.lastMonthPaidInvoicesCount)} paid last month`
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              icon={<HourglassEmpty fontSize="small" />}
              label="Last Month Pending Revenue"
              value={invoicesError ? <ErrorFallback>—</ErrorFallback> : currency(summary.lastMonthPendingRevenue)}
              sub={`${invoiceCount(summary.lastMonthPendingInvoicesCount)} open from last month`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              tone={summary.overdueRevenue > 0 ? "error" : "neutral"}
              icon={<Warning fontSize="small" />}
              label="Overdue Revenue"
              value={invoicesError ? <ErrorFallback>—</ErrorFallback> : currency(summary.overdueRevenue)}
              sub={
                  summary.outstandingAmount > 0
                      ? `${invoiceCount(summary.overdueInvoicesCount)} past due · ${currency(summary.outstandingAmount)} still uncollected`
                      : `${invoiceCount(summary.overdueInvoicesCount)} past due`
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              icon={<Description fontSize="small" />}
              label="Total Pending Revenue"
              value={invoicesError ? <ErrorFallback>—</ErrorFallback> : currency(summary.pendingAmount)}
              sub={`${invoiceCount(summary.pendingInvoices)} open`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              icon={<CalendarToday fontSize="small" />}
              label="Recurring Revenue"
              value={chargesError ? <ErrorFallback>—</ErrorFallback> : currency(summary.recurringRevenue)}
              sub="Monthly recurring Charges"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              icon={<People fontSize="small" />}
              label="Total Active Clients"
              value={invoicesError ? <ErrorFallback>—</ErrorFallback> : summary.activeClients}
              sub="With billing activity"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              icon={<People fontSize="small" />}
              label="Last Month Active Clients"
              value={invoicesError ? <ErrorFallback>—</ErrorFallback> : summary.lastMonthActiveClients}
              sub="Clients invoiced last month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              size="large"
              icon={<CalendarToday fontSize="small" />}
              label="Avg Days to Payment"
              value={invoicesError ? <ErrorFallback>—</ErrorFallback> : summary.avgDaysToPayment}
              sub="Last month avg payment time"
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <Card>
              <CardHeader title="Recent Invoices" subheader="Recent invoices created in the last 30 days" />
              <CardContent>
                {isLoadingInvoices && recentInvoices.length === 0 ? (
                  <CenteredLoading>
                    <CircularProgress />
                  </CenteredLoading>
                ) : recentInvoices.length > 0 ? (
                  <>
                    <InvoiceList>
                      {recentInvoices.map((invoice) => (
                        <InvoiceListRow key={invoice._id || invoice.id}>
                          <Box>
                            <Typography variant="body1">
                              {invoice.client?.name || 'Unknown Client'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Invoice #{invoice.invoiceNumber}
                            </Typography>
                          </Box>
                          <InvoiceListRowMeta>
                            <RightAligned>
                              <Typography variant="body1">
                                {(invoice.amount || 0).toLocaleString("en-US", {style:"currency", currency:"USD"})}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDayShort(invoice.invoiceDate)}
                              </Typography>
                            </RightAligned>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/billing/invoices/${invoice._id}`)}
                            >
                              <Description fontSize="small" />
                            </IconButton>
                          </InvoiceListRowMeta>
                        </InvoiceListRow>
                      ))}
                    </InvoiceList>
                    {invoiceTotal > pageSize && (
                      <PaginationRow>
                        <Pagination
                          count={Math.ceil(invoiceTotal / pageSize)}
                          page={invoicePage}
                          onChange={handleInvoicePageChange}
                          color="primary"
                          size="small"
                        />
                      </PaginationRow>
                    )}
                  </>
                ) : (
                  <EmptyMessage variant="body2" color="text.secondary">
                    No recent invoices
                  </EmptyMessage>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card>
              <CardHeader title="Upcoming Recurring Charges" subheader="Scheduled recurring charges for the next 30 days" />
              <CardContent>
                {isLoadingCharges && upcomingCharges.length === 0 ? (
                  <CenteredLoading>
                    <CircularProgress />
                  </CenteredLoading>
                ) : upcomingCharges.length > 0 ? (
                  <InvoiceList>
                    {upcomingCharges.map((charge) => (
                      <InvoiceListRow key={charge._id || charge.id}>
                        <Box>
                          <Typography variant="body1">
                            {charge.client?.name || 'Unknown Client'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {charge.name || charge.description}
                          </Typography>
                        </Box>
                        <RightAligned>
                          <Typography variant="body1">
                            {(charge.amount || 0).toLocaleString("en-US", {style:"currency", currency:"USD"})}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDayShort(charge.nextDate || charge.date)}
                          </Typography>
                        </RightAligned>
                      </InvoiceListRow>
                    ))}
                  </InvoiceList>
                ) : (
                  <EmptyMessage variant="body2" color="text.secondary">
                    No upcoming recurring charges
                  </EmptyMessage>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </StyledTabPanel>
    );
}

export default BillingOverview;
